import { motion } from 'framer-motion';
import { Shield, AlertCircle, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function KYCPromptModal({ isOpen, onClose, onStartKYC, rejectionReason }) {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="pt-8 pb-4 flex justify-center">
          <div className="w-16 h-16 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
            <Shield className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 text-center">
          <h2 className="text-xl font-medium text-white mb-2" data-testid="kyc-prompt-title">
            Complete KYC to Invest
          </h2>
          <p className="text-white/60 text-sm mb-6">
            As per SEBI regulations, we need to verify your identity before you can invest in properties.
          </p>

          {rejectionReason && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-left">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-400 font-medium">Previous KYC Rejected</p>
                  <p className="text-xs text-white/60 mt-1">{rejectionReason}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4 text-left mb-6">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-xs text-white/60">1</div>
              <div>
                <p className="text-sm text-white">Personal Details</p>
                <p className="text-xs text-white/40">PAN & Aadhar verification</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-xs text-white/60">2</div>
              <div>
                <p className="text-sm text-white">Address</p>
                <p className="text-xs text-white/40">Your residential address</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-xs text-white/60">3</div>
              <div>
                <p className="text-sm text-white">Bank Details</p>
                <p className="text-xs text-white/40">For receiving payouts</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              className="flex-1 text-white/60 hover:text-white hover:bg-white/10"
            >
              Maybe Later
            </Button>
            <Button
              onClick={onStartKYC}
              className="flex-1 bg-white text-black hover:bg-gray-200"
              data-testid="kyc-start-btn"
            >
              Start KYC <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </motion.div>
    </motion.div>
  );
}
